import chroma from 'chroma-js'
import dayjs from 'dayjs'
import parse from 'html-react-parser'
import { Helmet } from 'react-helmet-async'
import { toast } from 'react-toastify'
import { isValidEmail } from 'src/clinic/views/onboardng/clinicBrand/components/utils'
import { getChangedNumberValueFromInput } from 'src/mentor/helpers/utilityFunctions'
import {
  APP_CONSTANTS,
  BOOKING_STATUSES,
  GENDER_LIST_ONBOARDING,
  STRING_ID_TYPES,
} from '../constants'

const emailRegExp = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/
const nameRegExp = /^[a-zA-Z]+(\s+[a-zA-Z]+)*(?:\s*(s\/o|d\/o|c\/o)\s*[a-zA-Z]+(\s+[a-zA-Z]+)*)?$/i
const onlyNumbersRegExp = /^[0-9]+$/

const FAKE_EVENT_OBJECT = { target: { id: null, value: null } }

export function getCSmartTableColumnData({ key, label, filter, sorter, _style }) {
  return {
    key,
    label,
    filter,
    sorter,
    _style,
  }
}

export const openInNewTab = (url) => {
  window.open(url, '_blank')
}

export function formatNumberWithMinTwoDigits(num) {
  return num?.toString()?.padStart(2, '0')
}

export function validateEmail(emailString) {
  if (emailRegExp.test(emailString)) {
    return true
  }
  return false
}

export function validateName(nameString) {
  if (nameRegExp.test(nameString)) {
    return true
  }
  return false
}

export function validateOnlyNumbers(nameString) {
  if (onlyNumbersRegExp.test(nameString)) {
    return true
  }
  return false
}

export function formatDateForPosts(inputDate) {
  const today = dayjs()
  const date = dayjs(inputDate, 'YYYY-MM-DD HH:mm:ss')

  if (date.isSame(today, 'day')) {
    return 'Today'
  } else if (date.isSame(today, 'year')) {
    return date.format('MMM D')
  } else {
    return date.format('MMM D, YYYY')
  }
}

// Helper function to get timezone offset in format like "+05:30" (replaces moment().format('Z'))
export function getTimezoneOffset() {
  const offset = -new Date().getTimezoneOffset()
  const sign = offset >= 0 ? '+' : '-'
  const absOffset = Math.abs(offset)
  const hours = Math.floor(absOffset / 60)
  const minutes = absOffset % 60
  return `${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

export function countWordsInHTML(htmlString) {
  // Remove HTML tags and extract text content
  const textContent = htmlString.replace(/<[^>]*>/g, '')
  // Split the text into words using whitespace as a delimiter
  const words = textContent.split(/\s+/)
  // Count the number of words
  return words.length
}

export function calculateReadTimeFromWords(words) {
  const wpm = 130
  const toReturn = Math.floor(parseInt(words) / wpm)
  if (toReturn > 0) {
    return toReturn
  } else {
    return 1
  }
}

export function generateUidForAgora() {
  const minUserID = 1
  const maxUserID = Math.pow(2, 32) - 1

  // Generate a random integer within the specified range
  const uid = Math.floor(Math.random() * (maxUserID - minUserID + 1)) + minUserID

  return uid // Convert the integer to a string
}

// Canonical public origin for absolute URLs (og:url, canonical, JSON-LD).
// Kept here (rather than only inside AumhumSite.jsx) so every page that
// imports SEOComponent gets the same origin without a second source of truth.
export const CANONICAL_ORIGIN = 'https://aumhum.com'

/**
 * Upgraded SEOComponent.
 *
 * Backward-compatible with the original 4-prop signature
 * `({ title, description, image, keywordsArr })`. Every new prop is
 * optional and has a sensible default.
 *
 * New props:
 *   - pathname  : override for the current location path; defaults to
 *                 window.location.pathname. Used to build canonical + og:url.
 *   - canonical : explicit absolute URL; wins over pathname if provided.
 *   - type      : og:type — "website" (default) | "article" | "profile" | etc.
 *   - locale    : og:locale — defaults to "en_IN" (matches AumhumHelmet).
 *   - noIndex   : boolean — emits `robots: noindex, nofollow` for
 *                 short-lived / private pages (/v/*, /rx/*, /receipt/*).
 *   - jsonLd    : single JSON-LD object or array of objects to emit as
 *                 `<script type="application/ld+json">` entries.
 *   - twitterSite / twitterCreator : optional twitter handles.
 */
export const SEOComponent = ({
  title,
  description,
  image,
  keywordsArr,
  pathname,
  canonical,
  type = 'website',
  locale = 'en_IN',
  noIndex = false,
  jsonLd,
  twitterSite,
  twitterCreator,
}) => {
  let keyWordsArray = [
    ...(Array.isArray(keywordsArr) ? keywordsArr : []),
    ...(APP_CONSTANTS.DEFAULT_KEYWORDS || []),
  ]
  const uniqueKeywordsSet = new Set(keyWordsArray)

  const titleString = title || APP_CONSTANTS.DEFAULT_TITLE
  const keywordsString = Array.from(uniqueKeywordsSet).join(', ')
  const descriptionString = description || APP_CONSTANTS.DEFAULT_DESCRIPTION
  const imageLink = image || APP_CONSTANTS.APP_LOGO_LINK

  // Build the canonical / og:url value. Explicit `canonical` wins; else
  // join the canonical origin with the provided/current pathname.
  const resolvedPathname =
    pathname ||
    (typeof window !== 'undefined' && window.location
      ? window.location.pathname
      : '/')
  const canonicalUrl =
    canonical ||
    `${CANONICAL_ORIGIN}${resolvedPathname.startsWith('/') ? resolvedPathname : '/' + resolvedPathname}`

  const jsonLdList = jsonLd
    ? Array.isArray(jsonLd)
      ? jsonLd.filter(Boolean)
      : [jsonLd]
    : []

  const robotsValue = noIndex
    ? 'noindex, nofollow'
    : 'index, follow, max-image-preview:large, max-snippet:-1'

  return (
    <Helmet>
      <title>{titleString}</title>
      <meta name="description" content={descriptionString} />
      <meta name="keywords" content={keywordsString} />
      <meta name="robots" content={robotsValue} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph — the canonical surface for social link previews. */}
      <meta property="og:site_name" content="AUMHUM" />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={titleString} />
      <meta property="og:description" content={descriptionString} />
      <meta property="og:image" content={imageLink} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:locale" content={locale} />

      {/* Twitter Card — large-image card so screenshot previews look right. */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={titleString} />
      <meta name="twitter:description" content={descriptionString} />
      <meta name="twitter:image" content={imageLink} />
      {twitterSite ? <meta name="twitter:site" content={twitterSite} /> : null}
      {twitterCreator ? (
        <meta name="twitter:creator" content={twitterCreator} />
      ) : null}

      {jsonLdList.map((obj, i) => (
        <script key={`jsonld-${i}`} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </Helmet>
  )
}

export function calculateMinWidthAndHeight(decimalAspectRatio) {
  if (decimalAspectRatio <= 0) {
    throw new Error('Aspect ratio must be a positive number.')
  }

  // Calculate the minimum width and height
  const gcd = (a, b) => (b < 1e-5 ? a : gcd(b, a % b))
  const denominator = 1 / decimalAspectRatio
  const minWidth = 1 / gcd(1, denominator)
  const minHeight = denominator / gcd(1, denominator)

  return { minWidth, minHeight }
}

export function checkAndGetTargetValue(e) {
  switch (e.target.type) {
    case 'checkbox':
      return e.target.checked
    case 'number':
      return e.target.value === '' ? null : Number(e.target.value)

    default:
      return e.target.value
  }
}

export function getFCMToken() {
  return localStorage.getItem('FCMToken')
}

export function removePlusFromBeginning(inputString) {
  if (inputString) {
    if (inputString?.startsWith('+')) {
      return inputString?.substring(1)
    } else {
      return inputString
    }
  }
}

export function identifyUUIDOrSlugType(str) {
  const uuidRegex = /^[a-zA-Z0-9]{3}-[0-9a-fA-F]{32}$/
  if (uuidRegex.test(str)) {
    return STRING_ID_TYPES.uuid
  } else {
    return STRING_ID_TYPES.slug
  }
}

export function separateItemsInArray(onBasisOfField, array) {
  const toReturn = {}
  for (let i = 0; i < array.length; i++) {
    // checking and adding categoryId to toReturn
    if (!toReturn.hasOwnProperty(array[i][onBasisOfField])) {
      toReturn[array[i][onBasisOfField]] = []
    }
    toReturn[array[i][onBasisOfField]] = [...toReturn[array[i][onBasisOfField]], array[i]]
  }
  return toReturn
}

export const defaultConfirmationModalProps = {
  visibility: false,
  visibilitySetter: null,
  action: '',
  body: '',
  buttonColor: 'success',
  onSubmitFunctions: [],
}

export function generateHexWithOpacity(opacity = 1) {
  // Generate a random color
  const randomColor = chroma.random()

  // Set the desired opacity
  const colorWithOpacity = chroma(randomColor).alpha(opacity)

  // Convert to hex and return
  return colorWithOpacity.hex()
}

export function generateHexWithLessBrightColors(opacity = 1) {
  let randomColor

  do {
    randomColor = chroma.random()
  } while (randomColor.luminance() > 0.85) // Avoid very bright colors

  return randomColor.alpha(opacity).hex()
}

function isColorUnique(color, existingColors) {
  // Check if the color is not similar to any existing color
  return !existingColors.some((existingColor) => chroma.deltaE(existingColor, color) < 20) // Adjust the threshold as needed
}

export const isLightColor = (color) => {
  let r, g, b

  if (color.startsWith('#')) {
    // Convert HEX to RGB
    color = color.replace('#', '')
    r = parseInt(color.substring(0, 2), 16)
    g = parseInt(color.substring(2, 4), 16)
    b = parseInt(color.substring(4, 6), 16)
  } else if (color.startsWith('rgb')) {
    // Extract RGB values from "rgb(r, g, b)"
    const match = color.match(/\d+/g)
    if (match) {
      ;[r, g, b] = match.map(Number)
    } else {
      return false // Default to dark if parsing fails
    }
  } else {
    return false // Default to dark if the format is unknown
  }

  // Calculate brightness using the luminance formula
  let brightness = (r * 299 + g * 587 + b * 114) / 1000

  return brightness > 155 // Returns true if the color is light
}

export function hexToRGBA(hex, alpha = 0.2) {
  if (typeof hex !== 'string' || !/^#([0-9A-Fa-f]{6})$/.test(hex)) {
    return `rgba(0, 0, 0, ${1})` // fallback color
  }

  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

/**
 * Generate a hex color code not similar to existing colors.
 * @param {number} opacity - Opacity value between 0 and 1 (default is 1).
 * @param {string[]} existingColors - Array of existing hex color codes.
 * @returns {string} Hex color code.
 */
export function generateUniqueHexWithOpacity(opacity = 1, existingColors = []) {
  let generatedColor

  // Retry until a color is found that is not similar to any in the existingColors array
  while (true) {
    generatedColor = generateHexWithOpacity(opacity)

    // Check if the generated color is not similar to any existing color
    const isUnique = isColorUnique(generatedColor, existingColors)

    if (isUnique) {
      break
    }
  }

  return generatedColor
}

export function array_move(arr, old_index, new_index) {
  if (new_index >= arr.length) {
    var k = new_index - arr.length + 1
    while (k--) {
      arr.push(undefined)
    }
  }
  arr.splice(new_index, 0, arr.splice(old_index, 1)[0])
  return arr
}

export function sortFn(obj1, obj2) {
  if (obj1 === obj2) {
    return 0
  } else {
    return obj1 > obj2 ? 1 : -1
  }
}

export function checkIfStringIsValid(str) {
  if (typeof str !== 'string') {
    return false
  }
  return str.length > 0
}

export function isValidNumber(value) {
  return typeof value === 'number' && Number.isFinite(value)
}

export function isValidInteger(value) {
  return isValidNumber(value) && Number.isInteger(value)
}

export function getValidNumberFromStringNumber(str) {
  const number = Number(str)
  return isValidInteger(number) ? number : null
}

export function getWhatsAppShareLink(text) {
  const isMobileOrTablet = /(android|iphone|ipad|mobile)/i.test(navigator.userAgent)

  if (isMobileOrTablet) {
    return `https://${isMobileOrTablet ? 'api' : 'web'}.whatsapp.com/send?text=${text}`
  } else {
    const userAgent = window.navigator.userAgent.toLowerCase()
    const isWhatsappInstalled = userAgent.includes('whatsapp')
    return `${isWhatsappInstalled ? 'whatsapp://?' : 'https://web.whatsapp.com/'}send?text=${text}`
  }
}

export function checkIfStringIsASlugOrId(str) {}

export function addToGoogleCalendarLink(
  utcStartDateTime,
  utcEndDateTime,
  name,
  description,
  moment,
) {
  const startDateString = dayjs?.utc(utcStartDateTime)?.format('YYYYMMDDTHHmmss[Z]')
  const endDateString = dayjs?.utc(utcEndDateTime)?.format('YYYYMMDDTHHmmss[Z]')
  const googleCalendarLink = `https://www.google.com/calendar/render?action=TEMPLATE&text=${name}&details=${description}&dates=${startDateString}/${endDateString}`
  return googleCalendarLink
}

export function genericHandleChange(event, setter) {
  if (event.target.id)
    setter((prev) => ({
      ...prev,
      [event.target.id]: checkAndGetTargetValue(event),
    }))
  else {
    setter(checkAndGetTargetValue(event))
  }
}

export function getCorrectTargetValueForEvent(e, overrideNumberTransform = false) {
  switch (e.target.type) {
    case 'checkbox':
      return e.target.checked
    case 'number':
      if (overrideNumberTransform) {
        return e.target.value
      }
      return getChangedNumberValueFromInput(e.target.value)

    default:
      return e.target.value
  }
}

export function giveGenericHandleChangedData(
  eventObjOrPrevFuncOrValueToSet,
  prev,
  overrideNumberTransform,
  objectIsNotEventObject = false,
) {
  if (typeof eventObjOrPrevFuncOrValueToSet === 'function') {
    return eventObjOrPrevFuncOrValueToSet(prev)
  } else {
    if (objectIsNotEventObject) {
      return eventObjOrPrevFuncOrValueToSet
    } else {
      if (eventObjOrPrevFuncOrValueToSet.target.id)
        return {
          ...prev,
          [eventObjOrPrevFuncOrValueToSet.target.id]: getCorrectTargetValueForEvent(
            eventObjOrPrevFuncOrValueToSet,
            overrideNumberTransform,
          ),
        }
      else {
        return getCorrectTargetValueForEvent(eventObjOrPrevFuncOrValueToSet)
      }
    }
  }
}

export function handleEventTargetChange(prev, event) {
  return {
    ...prev,
    [event.target.id]: getCorrectTargetValueForEvent(event),
  }
}

export function genericChangeNestedFields(value, outerId, setter) {
  setter((prev) => ({
    ...prev,
    [outerId]: typeof value === 'function' ? value(prev?.[outerId]) : value,
  }))
}

export function genericHandleNestedFields(event, outerId, setState) {
  if (typeof event === 'function') {
    genericChangeNestedFields((prev) => event(prev), outerId, setState)
  } else {
    genericChangeNestedFields((prev) => handleEventTargetChange(prev, event), outerId, setState)
  }
}

export function genericHandleNestedNestedFields(event, outerId, outerOuterId, setter) {
  setter((prev) => ({
    ...prev,
    [outerOuterId]: {
      ...(prev?.[outerOuterId] || {}),
      [outerId]: {
        ...(prev?.[outerOuterId]?.[outerId] || {}),
        [event.target.id]: giveGenericHandleChangedData(event, prev?.[outerOuterId]?.[outerId]),
      },
    },
  }))
}

export function titleCaseWithExceptionWords(str, exceptions = []) {
  // Split the string into words
  const words = str.split(' ')

  // Convert each word to title case except the exceptions
  const titleCaseWords = words.map((word) => {
    // Check if the word is in exceptions
    if (exceptions.includes(word.toLowerCase())) {
      return word // Leave the word as is
    } else {
      // Convert the word to title case
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    }
  })

  // Join the title case words back into a string
  return titleCaseWords.join(' ')
}

export function getParseDataFromHtmlString(htmlString, otherParams = { format: true }) {
  const { format, preserveEmptyParagraphs = false } = otherParams

  let formattedString = htmlString
  if (format) {
    // Clean up TipTap HTML for better printing
    formattedString = (htmlString || '')
      // Trailing/leading whitespace in paragraphs
      .replace(/<p>\s+/g, '<p>')
      .replace(/\s+<\/p>/g, '</p>')
      // Normalize multiple spaces to single space
      .replace(/\s{2,}/g, ' ')
      // Remove empty list items
      .replace(/<li>\s*<\/li>/g, '')
    if (!preserveEmptyParagraphs) {
      // Remove empty paragraphs (skip when preserving blank lines, e.g. free-form rich text)
      formattedString = formattedString
        .replace(/<p><\/p>\n?/g, '')
        .replace(/<p>\s*<\/p>/g, '')
        .replace(/(<p><\/p>)+/g, '')
    }
  }
  return parse(formattedString)
}

export function giveMediaTypeBasedOnMimeType(mimeType) {
  if (mimeType.includes('image')) {
    return 'image'
  } else if (mimeType.includes('video')) {
    return 'video'
  } else if (mimeType.includes('audio')) {
    return 'audio'
  } else {
    return 'file'
  }
}

export function getVideoFileMetaData(file) {
  return new Promise((resolve, reject) => {
    var video = document.createElement('video')
    video.preload = 'metadata'

    video.onloadedmetadata = function () {
      window.URL.revokeObjectURL(video.src)
      resolve({
        duration: video.duration,
        width: video.videoWidth,
        height: video.videoHeight,
      })
    }

    video.onerror = function () {
      reject('Error loading video file')
    }

    video.src = URL.createObjectURL(file)
  })
}

export function filterValidAndNonRepeatedEmails(emails) {
  return [...new Set(emails || [])].filter((email) => !!email && isValidEmail(email))
}

export function areStringsEqualIgnoreCase(str1, str2) {
  if (str1 === undefined || str1 === null || str2 === undefined || str2 === null) {
    return false
  }
  return str1.toLowerCase() === str2.toLowerCase()
}

export async function loadOptionsForAsyncPaginate(
  search,
  loadedOptions,
  {
    getFunction,
    apiUrl,
    otherParams,
    transformDataToReturn = (data) => data,
    setLoading = () => {},
    ...additional
  },
) {
  setLoading && setLoading(true)
  const resp = await getFunction(apiUrl, {
    page: additional?.page || 0,
    search: search,
    ...otherParams,
  })
  var dataToReturn = transformDataToReturn(resp.data)
  setLoading && setLoading(false)
  const hasMore = resp.data?.length > 0

  return {
    options: dataToReturn,
    hasMore: hasMore,
    additional: {
      page: additional?.page + 1,
      getFunction,
      apiUrl,
      otherParams,
      setLoading,
      transformDataToReturn,
      ...additional,
    },
  }
}

export function prospectContactIsEmpty(prospect) {
  return !prospect?.email && !prospect?.mobile
}

export function workforceContactIsEmpty(workforce) {
  return !workforce?.id && !workforce?.uid && !workforce?.userId
}

export function getPercentage(num, denom) {
  if (!denom) return null
  return parseFloat(((num / denom) * 100).toFixed(2))
}

export async function downloadExcelSheetFromRows({ rows, titleOfSheet, workSheetTitle }) {
  const XLSX = await import('xlsx') // Dynamically import
  const worksheet = XLSX.utils.json_to_sheet(rows)
  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, worksheet, workSheetTitle)
  XLSX.writeFile(workbook, `${titleOfSheet}.xlsx`, { compression: true })
}

export async function downloadExcelSheetFromSheetJsWbData({ workbookData, titleOfSheet }) {
  const XLSX = await import('xlsx') // Dynamically import
  XLSX.writeFile(workbookData, `${titleOfSheet}.xlsx`, { compression: true })
}

export function convertAssessmentIdBasedCategoryListToMetaCategoryList(list) {
  const newList = []
  list?.forEach((item) => {
    newList.push(convertAssessmentIdBasedCategoryDataToCategoriesObject(item))
  })
  return newList
}

function convertAssessmentIdBasedCategoryDataToCategoriesObject(data) {
  return {
    apiParam: 'categories',
    id: data?.assessmentId,
    enabled: true,
    deleted: false,
    title: data?.title,
    coverIcon: data?.coverIcon,
    coverImage: data?.coverImage,
    name: data?.title,
    // internalTitle: 'Guided Meditations',
    // singleTitle: 'Guided Meditation',
    categoryType: data?.customFields?.categoryType,
    // player: false,
    // icon: 'ic_stones',
    // appListable: true,
    // orderId: 0,
    // emptyPageScreen: {
    //   title: 'No Courses Added',
    //   subtext: 'When you start a guided meditation course, it would show up here',
    //   btnText: 'Explore Guided Meditations',
    //   images: [
    //     'MEDIA-ca6cae93a8a043f2a0136b1f28fb9c06',
    //     'MEDIA-dd8e571b5a174583ad5082cec276c34d',
    //     'MEDIA-a1517c5fa97949e482511ec40b32668d',
    //   ],
    // },
    // translations: {
    //   en: {
    //     name: 'Guided Meditations',
    //     singleTitle: 'Guided Meditation',
    //     emptyPageTitle: 'No Courses Added',
    //     emptyPageSubtext: 'When you start a guided meditation course, it would show up here',
    //     emptyPageBtnText: 'Explore Guided Meditations',
    //     idField: 'Guided Meditations',
    //   },
    // },
    // featureCategory: false,
    // defaultPlayer: 'full_length_player',
    // playerType: 'regular',
    // endJournal: true,
    // plan: true,
    // tabs: ['meditation', 'sleep'],
    // tab: 'meditation',
    // bgMusic: true,
    // maxMusicVolume: 30,
    // minMusicVolume: 1,
    // maxVoiceVolume: 100,
    // minVoiceVolume: 1,
    // sliderPosition: 80,
    // slides: [
    //   {
    //     fileId: 'MEDIA-846bac5045844ecdaa2646943155218c',
    //     inAt: 0,
    //     outAt: 9999,
    //     primaryColor: '#976f26',
    //     secondaryColor: '#042135',
    //     preselected: false,
    //   },
    //   {
    //     fileId: 'MEDIA-a1fa992a66f049f79eb75025408c4617',
    //     inAt: 10000,
    //     outAt: 19999,
    //     primaryColor: '#5c565b',
    //     secondaryColor: '#06060a',
    //     preselected: false,
    //   },
    // ],
    // continuePlayType: 'back_to_back',
    // audioContinuePlayType: 'result_screen',
    // portraitVideoContinuePlayType: 'result_screen',
    // landscapeVideoContinuePlayType: 'result_screen',
    // loopedContinuePlayType: 'result_screen',
    // itemType: 'category',
  }
}

export function getNumberStringWithCommas(x) {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

export function removeKeyFromObject(obj, keyToRemove) {
  const { [keyToRemove]: _, ...rest } = obj
  return rest
}

export function getOrdinalSuffixForSuperScript(number) {
  if (typeof number !== 'number' || number < 1) {
    return ''
  }

  const remainder10 = number % 10
  const remainder100 = number % 100

  if (remainder100 >= 11 && remainder100 <= 13) {
    return 'th'
  }

  switch (remainder10) {
    case 1:
      return 'st'
    case 2:
      return 'nd'
    case 3:
      return 'rd'
    default:
      return 'th'
  }
}

export function getFirstLetterCapitalizedString(str) {
  return str ? str.charAt(0).toUpperCase() + str.slice(1) : ''
}

export function shouldLogOutBasedOnHttpResponseStatus(statusCode) {
  if (statusCode === 401) {
    return true
  }
  return false
}

export const hasDuplicateStrings = (strings) => {
  console.log({ strings })
  const stringSet = new Set(strings)

  console.log({ stringSet })
  return !(stringSet.size === strings.length)
}

export const isTheDuplicateStringInList = ({ strings, stringToCheck }) => {
  if (!Array.isArray(strings) || typeof stringToCheck !== 'string') {
    // console.error("Invalid input: 'strings' must be an array and 'stringToCheck' must be a string.");
    return false
  }

  if (!hasDuplicateStrings(strings)) {
    console.log('no duplicate strings')
    return false
  }

  // Count occurrences of stringToCheck in the list
  const occurrences = strings.filter((str) => str === stringToCheck).length

  console.log({ stringToCheck, occurrences })

  // Return true if the stringToCheck is duplicated
  return occurrences > 1
}

export function getEventTargetValueFromMuiToggleGroup(enforceValue, e, value) {
  if (enforceValue && value === null) {
    return null
  }
  const eventId = e.target.id
  console.log({ eventId, e })

  let fakeEventVObj = { ...FAKE_EVENT_OBJECT }

  fakeEventVObj.target.id = eventId
  fakeEventVObj.target.value = value

  console.log({ fakeEventVObj })

  return fakeEventVObj
}
export function getOrdinalSuffix(num) {
  if (typeof num !== 'number' || isNaN(num)) return ''

  const suffixes = ['th', 'st', 'nd', 'rd']
  const v = num % 100

  return num + (suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0])
}

export function getGenderFromGenderId(genderId) {
  const foundGender = GENDER_LIST_ONBOARDING.find((gdr) => gdr?.value === genderId)
  return foundGender
}

export function maskString(stringToMask) {
  if (!stringToMask) return ''
  if (stringToMask.length <= 2) return stringToMask // If name is too short, return as is

  return `${stringToMask[0]}*******${stringToMask[stringToMask.length - 1]}`
}

export function copyToClipBoard(field, text) {
  navigator.clipboard.writeText(text).then(
    () => {
      toast.success(`${field} Copied to clipboard successfully!`)
    },
    () => {
      toast.error(`Could not copy ${field} please try again`)
    },
  )
}

export function getExactDiffFromCurrentTimeDayJsWithExtraData(
  endMoment,
  compareWith = dayjs(),
  includeRelativeWords = true,
) {
  const now = compareWith.startOf('day')
  const target = endMoment.startOf('day')

  const daysDiff = target.diff(now, 'days')
  const isPast = target.isBefore(now)

  if (daysDiff === 0) return { differenceString: 'Today', isPastDate: false }
  if (daysDiff === -1) return { differenceString: 'Yesterday', isPastDate: true }
  if (daysDiff === 1) return { differenceString: 'Tomorrow', isPastDate: false }

  // Calculate total days first
  const totalDays = Math.abs(daysDiff)

  // Convert to years/months/days
  const years = Math.floor(totalDays / 365)
  const remainingDaysAfterYears = totalDays % 365
  const months = Math.floor(remainingDaysAfterYears / 30)
  const days = remainingDaysAfterYears % 30

  const parts = []
  if (years) parts.push(`${years} year${years !== 1 ? 's' : ''}`)
  if (months) parts.push(`${months} month${months !== 1 ? 's' : ''}`)
  if (days || parts.length === 0) parts.push(`${days} day${days !== 1 ? 's' : ''}`)

  let differenceString = parts.join(' ')
  if (includeRelativeWords) {
    differenceString = isPast ? `${differenceString} ago` : `in ${differenceString}`
  }

  return {
    differenceString,
    isPastDate: isPast,
  }
}

export function getExactDiffFromCurrentTimeDayJs(
  endMoment,
  compareWith = dayjs(),
  includeRelativeWords = true,
) {
  return getExactDiffFromCurrentTimeDayJsWithExtraData(endMoment, compareWith, includeRelativeWords)
    .differenceString
}

export function getExactTimeDiff(
  endMoment,
  compareWith = dayjs(),
  includeRelativeWords = true,
  showDateIfNotToday = true,
  showSeconds = true,
) {
  const now = compareWith
  const target = endMoment

  if (target.isSame(now, 'day')) {
    let diffSeconds = target.diff(now, 'seconds')

    const isPast = diffSeconds < 0
    diffSeconds = Math.abs(diffSeconds)

    const hours = Math.floor(diffSeconds / 3600)
    const minutes = Math.floor((diffSeconds % 3600) / 60)
    const seconds = diffSeconds % 60

    const parts = []
    if (hours) parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`)
    if (minutes) parts.push(`${minutes} minute${minutes !== 1 ? 's' : ''}`)
    if (showSeconds && (seconds || parts.length === 0)) {
      parts.push(`${seconds} second${seconds !== 1 ? 's' : ''}`)
    }

    let result = parts.join(' ')
    if (includeRelativeWords) {
      result = isPast ? `${result} ago` : `in ${result}`
    }

    return result
  }

  if (!showDateIfNotToday) {
    return `${target.format('h:mm A')}`
  }

  const dateDiff = getExactDiffFromCurrentTimeDayJsWithExtraData(
    target,
    compareWith,
    includeRelativeWords,
  )
  return `${dateDiff.differenceString} at ${target.format('h:mm A')}`
}

export const formatPhoneNumber = (phoneString) => {
  const match = phoneString.match(/^\+(\d{1,3})(\d{3})(\d{3})(\d{4})$/)
  const format2Match = phoneString.match(/^\+(\d{1,3})(\d{5})(\d{5})$/)
  if (!match) return { concatenatedString: phoneString } // Return original if format is invalid

  const countryCode = match[1]
  const part1 = match[2]
  const part2 = match[3]
  const part3 = match[4]

  // Only access format2Match properties if it's not null
  const format2CountryCode = format2Match ? format2Match[1] : null
  const format2Part1 = format2Match ? format2Match[2] : null
  const format2Part2 = format2Match ? format2Match[3] : null

  return {
    countryCodeWithoutPlus: countryCode,
    countryCode: `+${countryCode}`,
    phoneNumber: `${part1}${part2}${part3}`, // Keeping raw phone number
    formattedPhoneNumber: `${part1}-${part2}-${part3}`,
    concatenatedString: `(+${countryCode}) ${part1}-${part2}-${part3}`,
    format2CountryCode,
    formatType2Number: format2Match ? `+${format2CountryCode}-${format2Part1}-${format2Part2}` : null,
  }
}

export function isSlotBlocked(bookedEventInfo) {
  return bookedEventInfo?.status === BOOKING_STATUSES.BOOKING_INITIATED
}

/**
 * Ensures a string starts with a + character
 * @param {string} inputString - The string to check (typically a phone number)
 * @returns {string} - The string with a leading + character
 */
export function ensurePlusPrefix(inputString) {
  // Handle null, undefined or empty string
  if (!inputString) {
    return inputString
  }

  // Check if the string already starts with a +
  if (inputString.startsWith('+')) {
    return inputString // Return as is
  } else {
    return `+${inputString}` // Add + prefix
  }
}

/**
 * Appends HTML content to existing HTML content properly
 * @param {string} existingHtml - The existing HTML content
 * @param {string} newHtml - The new HTML content to append
 * @returns {string} - The combined HTML content
 */
export function appendHtmlContent(
  existingHtml = '',
  newHtml = '',
  isNewContentRegularString = true,
) {
  // Handle empty cases
  if (!existingHtml && !newHtml) return ''
  if (!existingHtml) return isNewContentRegularString ? `<p>${newHtml}</p>` : newHtml
  if (!newHtml) return existingHtml

  // Remove any trailing whitespace/newlines from existing content
  const trimmedExisting = existingHtml.trim()
  const trimmedNew = newHtml.trim()

  // Wrap the new content in a paragraph tag and append it
  return `${trimmedExisting}<p>${trimmedNew}</p>`
}

// export function prepareMentorCategorySuffix(mentor){

// }
